import type { CSSProperties } from 'react';
import { Loader2, Mail, UserCheck } from 'lucide-react';

type ApprovalFallbackActionsProps = {
  emailLoading: boolean;
  superiorLoading: boolean;
  isTestAccessMode: boolean;
  emailError?: string;
  superiorError?: string;
  generalError?: string;
  superiorButtonLabel?: string;
  cancelLabel?: string;
  alwaysShowCancel?: boolean;
  onRequestEmailCode: () => void;
  onRequestSuperiorApproval: () => void;
  onTestAccess: () => void;
  onCancel?: () => void;
};

export function ApprovalFallbackActions({
  emailLoading,
  superiorLoading,
  isTestAccessMode,
  emailError,
  superiorError,
  generalError,
  superiorButtonLabel,
  cancelLabel = 'Voltar ao Login',
  alwaysShowCancel = false,
  onRequestEmailCode,
  onRequestSuperiorApproval,
  onTestAccess,
  onCancel,
}: ApprovalFallbackActionsProps) {
  const label = superiorButtonLabel || (isTestAccessMode ? 'Simular liberação de acesso' : 'Solicitar liberação ao superior');
  const showCancel = alwaysShowCancel || Boolean(onCancel);

  return (
    <div style={containerStyle}>
      <div style={sectionTitleStyle}>Outras formas de acesso</div>

      <button
        onClick={onRequestEmailCode}
        disabled={emailLoading}
        style={{
          ...primaryActionStyle,
          background: '#6c5ce7',
          fontWeight: 700,
          opacity: emailLoading ? 0.7 : 1,
        }}
      >
        {emailLoading ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />}
        Receber código no meu e-mail
      </button>

      <button
        onClick={isTestAccessMode ? onTestAccess : onRequestSuperiorApproval}
        disabled={superiorLoading}
        style={{
          ...primaryActionStyle,
          background: isTestAccessMode ? '#e5e7eb' : '#2563eb',
          color: isTestAccessMode ? '#111827' : '#ffffff',
          border: isTestAccessMode ? '1px solid #cbd5e1' : 'none',
          fontWeight: 800,
          boxShadow: isTestAccessMode ? 'none' : '0 10px 24px rgba(37, 99, 235, 0.28)',
          opacity: superiorLoading ? 0.7 : 1,
        }}
      >
        {superiorLoading ? <Loader2 size={16} className="animate-spin" /> : <UserCheck size={16} />}
        {label}
      </button>

      {emailError && <p className="form-error-text" style={errorStyle}>{emailError}</p>}
      {superiorError && <p className="form-error-text" style={errorStyle}>{superiorError}</p>}
      {generalError && <p className="form-error-text" style={errorStyle}>{generalError}</p>}

      {showCancel && (
        <button
          onClick={onCancel}
          style={{
            ...cancelButtonStyle,
            width: '100%',
          }}
        >
          {cancelLabel}
        </button>
      )}
    </div>
  );
}

const containerStyle: CSSProperties = {
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  marginTop: 16,
};

const sectionTitleStyle: CSSProperties = {
  color: '#334155',
  fontSize: '0.72rem',
  fontWeight: 900,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  textAlign: 'left',
};

const primaryActionStyle: CSSProperties = {
  width: '100%',
  padding: '12px',
  borderRadius: 12,
  color: '#fff',
  border: 'none',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  fontSize: '0.82rem',
};

const cancelButtonStyle: CSSProperties = {
  padding: '10px 28px',
  borderRadius: 12,
  border: '1.5px solid #e2e8f0',
  background: '#f8fafc',
  color: '#64748b',
  cursor: 'pointer',
  fontSize: '0.82rem',
  fontWeight: 600,
  marginTop: 8,
  transition: 'all 0.2s',
};

const errorStyle: CSSProperties = {
  margin: '4px 0 0',
};
